import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  AlertTriangleIcon,
  ChevronRightIcon,
  FileCheckIcon,
  LayersIcon,
  PenSquareIcon,
  SquareCheckIcon,
} from "lucide-react";
import type { Key } from "react-aria";
import { Disclosure, DisclosurePanel } from "react-aria-components";

import { getDonorIds } from "@blis/shared/models/subject";

import { RoleCondition } from "@/components/RoleCondition";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DataList,
  DataListItem,
  DataListLabel,
  DataListValue,
} from "@/components/ui/data-list";
import { Tab, Tabs, TabsList } from "@/components/ui/tabs";
import type { TSubjectDetail } from "@/lib/types";
import { useIsMobile } from "@/lib/useIsMobile";
import { format } from "@/lib/utils/date";

export function Subject({ subject }: { subject: TSubjectDetail }) {
  const ids = getDonorIds(subject);
  const unosIds = subject.ids.filter((id) => id.type === "unosId");
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const isMobile = useIsMobile();

  // Determine which tab is active based on the current path
  const activeTab = currentPath.includes("/accession")
    ? "accession"
    : currentPath.includes("/report")
      ? "report"
      : currentPath.includes("/results")
        ? "results"
        : currentPath.includes("/samples")
          ? "samples"
          : currentPath.includes("/create")
            ? "create"
            : "accession"; // Default tab

  const handleTabChange = (value: Key) => {
    switch (value) {
      case "accession":
        navigate({ to: "/subjects/$id/accession", params: { id: subject.id } });
        break;
      case "report":
        navigate({ to: "/subjects/$id/report", params: { id: subject.id } });
        break;
      case "results":
        navigate({ to: "/subjects/$id/results", params: { id: subject.id } });
        break;
      case "samples":
        navigate({ to: "/subjects/$id/samples", params: { id: subject.id } });
        break;
      case "create":
        navigate({ to: "/subjects/$id/create", params: { id: subject.id } });
        break;
    }
  };

  return (
    <div className="flex-1 min-w-0 w-full overflow-hidden flex flex-col">
      {/* Donor Information Card */}
      <Card className="mx-4 mt-4 mb-2">
        <div className="p-2 sm:p-4">
          {isMobile ? (
            <>
              {/* Mobile View with Disclosure */}
              <div className="flex items-center justify-between mb-2">
                <div className="grid grid-cols-2 gap-4 w-full">
                  <DataList
                    orientation="vertical"
                    className="grid-cols-[80px_1fr]!"
                  >
                    <DataListItem>
                      <DataListLabel>Donor ID</DataListLabel>
                      <DataListValue copyable>{ids.caseId}</DataListValue>
                    </DataListItem>
                  </DataList>
                  <DataList
                    orientation="vertical"
                    className="grid-cols-[80px_1fr]!"
                  >
                    <DataListItem>
                      <DataListLabel>UNOS ID</DataListLabel>
                      <DataListValue
                        copyable
                        className="flex items-center gap-1"
                      >
                        {unosIds.length > 0
                          ? unosIds.map((id) => id.value).join(", ")
                          : "-"}
                        {unosIds.length > 1 && (
                          <AlertTriangleIcon className="h-3 w-3 text-yellow-500 shrink-0" />
                        )}
                      </DataListValue>
                    </DataListItem>
                  </DataList>
                </div>
              </div>
              <Disclosure className="group">
                <Button
                  slot="trigger"
                  variant="ghost"
                  className="w-full justify-start p-1 h-auto text-muted-foreground"
                >
                  <ChevronRightIcon className="size-4 transition-all group-data-[expanded=true]:rotate-90" />
                  More details
                </Button>
                <DisclosurePanel>
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    <DataList
                      orientation="vertical"
                      className="grid-cols-[80px_1fr]!"
                    >
                      <DataListItem>
                        <DataListLabel>Alternate IDs</DataListLabel>
                        <DataListValue copyable>
                          {ids.donorIds && ids.donorIds.length > 0
                            ? ids.donorIds?.join(", ")
                            : "-"}
                        </DataListValue>
                      </DataListItem>
                    </DataList>
                    <DataList
                      orientation="vertical"
                      className="grid-cols-[80px_1fr]!"
                    >
                      <DataListItem>
                        <DataListLabel>DOB</DataListLabel>
                        <DataListValue>
                          {subject.dob
                            ? format(subject.dob, { time: false })
                            : "-"}
                        </DataListValue>
                      </DataListItem>
                    </DataList>
                    <DataList
                      orientation="vertical"
                      className="grid-cols-[80px_1fr]!"
                    >
                      <DataListItem>
                        <DataListLabel>Sex</DataListLabel>
                        <DataListValue>{subject.sex ?? "-"}</DataListValue>
                      </DataListItem>
                    </DataList>
                  </div>
                </DisclosurePanel>
              </Disclosure>
            </>
          ) : (
            /* Desktop View - All fields in grid */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-6">
              <DataList
                orientation="vertical"
                className="grid-cols-[80px_1fr]!"
              >
                <DataListItem>
                  <DataListLabel>Donor ID</DataListLabel>
                  <DataListValue copyable>{ids.caseId}</DataListValue>
                </DataListItem>
              </DataList>
              <DataList
                orientation="vertical"
                className="grid-cols-[80px_1fr]!"
              >
                <DataListItem>
                  <DataListLabel>UNOS ID</DataListLabel>
                  <DataListValue copyable className="flex items-center gap-1">
                    {unosIds.length > 0
                      ? unosIds.map((id) => id.value).join(", ")
                      : "-"}
                    {unosIds.length > 1 && (
                      <AlertTriangleIcon className="h-3 w-3 text-yellow-500 shrink-0" />
                    )}
                  </DataListValue>
                </DataListItem>
              </DataList>
              <DataList
                orientation="vertical"
                className="grid-cols-[80px_1fr]!"
              >
                <DataListItem>
                  <DataListLabel>Alternate IDs</DataListLabel>
                  <DataListValue copyable>
                    {ids.donorIds && ids.donorIds.length > 0
                      ? ids.donorIds?.join(", ")
                      : "-"}
                  </DataListValue>
                </DataListItem>
              </DataList>
              <DataList
                orientation="vertical"
                className="grid-cols-[80px_1fr]!"
              >
                <DataListItem>
                  <DataListLabel>DOB</DataListLabel>
                  <DataListValue>
                    {subject.dob ? format(subject.dob, { time: false }) : "-"}
                  </DataListValue>
                </DataListItem>
              </DataList>
              <DataList
                orientation="vertical"
                className="grid-cols-[80px_1fr]!"
              >
                <DataListItem>
                  <DataListLabel>Sex</DataListLabel>
                  <DataListValue>{subject.sex ?? "-"}</DataListValue>
                </DataListItem>
              </DataList>
            </div>
          )}
        </div>
      </Card>

      {/* Navigation Tabs */}
      <div className="px-4 border-b">
        <Tabs selectedKey={activeTab} onSelectionChange={handleTabChange}>
          <TabsList>
            <Tab id="accession">
              <LayersIcon />
              <span>Accessions</span>
            </Tab>
            <Tab id="report">
              <FileCheckIcon />
              <span>Reports</span>
            </Tab>
            {/* <Tab id="samples">
              <TestTubesIcon />
              <span>Samples</span>
            </Tab> */}
            {/* <Tab id="results">
              <SquareCheckIcon />
              <span>Results</span>
            </Tab> */}
            <RoleCondition
              roles={[
                "blis_lab_assistant",
                "blis_lab_tech",
                "blis_lab_manager",
              ]}
            >
              <Tab id="results">
                <SquareCheckIcon />
                <span>Results</span>
              </Tab>
            </RoleCondition>
            <RoleCondition
              roles={[
                "blis_org_tech",
                "blis_lab_assistant",
                "blis_lab_tech",
                "blis_lab_manager",
              ]}
            >
              <Tab id="create">
                <PenSquareIcon />
                <span>Order</span>
              </Tab>
            </RoleCondition>
          </TabsList>
        </Tabs>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
